import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { GRID_COLUMNS } from "./constants";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import type { BuildingDataAttributeUpdate } from "@/stores/admin/models";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { CreateAttributeDialog } from "../CreateAttributeDialog";
import { AttributeTableRow } from "./AttributeTableRow";
import { ValidationDialog } from "./ValidationDialog";
import {
  useAttributeUpdates,
  useAttributeRelationships,
  useDocumentCategories,
  useAttributeFilter,
} from "./hooks";
import type { SubClassRow } from "@/stores/admin/models";
import * as repo from "@/stores/admin/repository";

const COLUMN_TOOLTIPS: Record<string, string> = {
  "Attribute Name":
    "Canonical identifier (primary key). Referenced by EHR mappings, validation rules, findings, and junction tables. Rename only via migration.",
  "Name (EN)":
    "English display name. Injected into the LLM extractor prompt so it can match English wording in documents. Also rendered in the building-data widget.",
  "Name (ET)":
    "Estonian display name. Injected into the LLM extractor prompt so it can match Estonian wording (e.g. 'hoone kõrgus') in documents.",
  Type: "Semantic category used to group attributes into sections on the building-data widget. Not sent to the LLM.",
  "Value Type":
    "Data primitive (decimal / integer / string / …). Enforced by data.validate_attribute_value() during document aggregation — invalid values are dropped. EHR ingest skips this check.",
  Unit: "Canonical measurement unit. Tells the LLM what unit to normalize to during extraction; rendered as suffix in the widget.",
  Widget:
    "If on, this attribute is eligible to appear on the building-data widget. Actual rendering also requires the widget reader to support it.",
  Sort: "Display order within the Type group on the widget (lower first).",
  Categories:
    "Document categories likely to contain this attribute. Injected into the LLM prompt as a search hint — the extractor prefers but isn't limited to these folders.",
  "Sub-Class":
    "Ontology sub-class(es). Drives extractor batching — attributes sharing a sub-class are sent to the LLM in the same request (batches of 8).",
  Validation:
    "Per-attribute rules (enum / range / length / pattern). Applied during document aggregation — invalid values are skipped. EHR ingest does NOT enforce these, and the LLM never sees them.",
  Actions:
    "Save persists pending edits to def_building_data_model. Delete removes the attribute and cascades through mappings, validations, and junctions.",
};

const HeaderWithTooltip = ({ label }: { label: string }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className="cursor-help underline decoration-muted-foreground/50 decoration-dotted underline-offset-4">
        {label}
      </span>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs leading-relaxed">
      {COLUMN_TOOLTIPS[label]}
    </TooltipContent>
  </Tooltip>
);

const AttributesTable = observer(() => {
  const { buildingDataAttributesStore } = useStores();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [widgetOnly, setWidgetOnly] = useState(false);
  const [savingAttribute, setSavingAttribute] = useState<string | null>(null);
  const [deletingAttribute, setDeletingAttribute] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState<string | null>(null);
  const [subClasses, setSubClasses] = useState<SubClassRow[]>([]);
  const [managingValidation, setManagingValidation] = useState<string | null>(null);

  const parentRef = useRef<HTMLDivElement>(null);

  // Custom hooks for cleaner code organization
  const { attributeUpdates, setAttributeUpdates, handleFieldChange } = useAttributeUpdates(
    buildingDataAttributesStore.attributesList
  );
  const { attributeCategories, setAttributeCategories, handleCategoriesChange } =
    useAttributeRelationships();
  const documentCategories = useDocumentCategories();
  const filterAttributes = useAttributeFilter(searchQuery);

  // Load all attribute categories when table loads
  const loadAllAttributeCategories = useCallback(async () => {
    try {
      const attributes = buildingDataAttributesStore.attributesList;
      const categoryMap: Record<string, number[]> = {};

      // Load categories for all attributes in parallel
      await Promise.all(
        attributes.map(async (attr) => {
          const categoryIds = await buildingDataAttributesStore.loadAttributeCategories(
            attr.attribute_nm
          );
          categoryMap[attr.attribute_nm] = categoryIds;
        })
      );

      // Update state with all loaded categories
      Object.entries(categoryMap).forEach(([attributeName, categoryIds]) => {
        setAttributeCategories((prev) => ({
          ...prev,
          [attributeName]: categoryIds,
        }));
      });
    } catch (error) {
      toast.error("Failed to load attribute categories");
    }
  }, [buildingDataAttributesStore, setAttributeCategories]);

  // Load initial data
  useEffect(() => {
    buildingDataAttributesStore.loadAttributes();
  }, [buildingDataAttributesStore]);

  // Load all attribute categories after attributes are loaded
  useEffect(() => {
    if (buildingDataAttributesStore.attributesList.length > 0) {
      loadAllAttributeCategories();
    }
  }, [buildingDataAttributesStore.attributesList.length, loadAllAttributeCategories]);

  // Load sub-classes (category definitions) + bulk relationship/validation maps
  useEffect(() => {
    const loadRelationships = async () => {
      try {
        const [allSubClasses] = await Promise.all([
          repo.listSubClasses(),
          buildingDataAttributesStore.loadAllAttributeSubClasses(),
          buildingDataAttributesStore.loadAllAttributeValidations(),
        ]);
        setSubClasses(allSubClasses);
      } catch (error) {
        console.error("Failed to load sub-classes or validations", error);
        toast.error("Failed to load sub-class or validation data");
      }
    };
    if (buildingDataAttributesStore.attributesList.length > 0) {
      loadRelationships();
    }
  }, [buildingDataAttributesStore.attributesList.length, buildingDataAttributesStore]);

  // Lazy load attribute categories when picker opens (fallback)
  const handleLoadCategories = useCallback(
    async (attributeName: string) => {
      if (attributeCategories[attributeName] !== undefined) return; // Already loaded

      try {
        const categoryIds =
          await buildingDataAttributesStore.loadAttributeCategories(attributeName);
        setAttributeCategories((prev) => ({
          ...prev,
          [attributeName]: categoryIds,
        }));
      } catch (error) {
        toast.error("Failed to load attribute categories");
      }
    },
    [attributeCategories, buildingDataAttributesStore]
  );

  // Handler for sub-class changes
  const handleSubClassesChange = useCallback(
    async (attributeName: string, subClassIds: (number | string)[]) => {
      try {
        await buildingDataAttributesStore.setAttributeSubClasses(
          attributeName,
          subClassIds as number[]
        );
        toast.success("Sub-classes updated");
      } catch (error) {
        console.error("Failed to update sub-classes", error);
        toast.error("Failed to update sub-classes");
      }
    },
    [buildingDataAttributesStore]
  );

  // Get filtered attribute list
  const filteredAttributes = useMemo(() => {
    let attrs = filterAttributes(buildingDataAttributesStore.attributesList);
    if (widgetOnly) {
      attrs = attrs.filter((attr) => attr.display_on_widget);
    }
    return attrs;
  }, [filterAttributes, buildingDataAttributesStore.attributesList, widgetOnly]);

  // Handler for group dropdown change
  const handleGroupChange = useCallback(
    (attributeName: string, groupId: number, groupName: string) => {
      setAttributeUpdates((prev) => ({
        ...prev,
        [attributeName]: {
          ...prev[attributeName],
          group_id: groupId,
          group_nm: groupName,
        },
      }));
    },
    [setAttributeUpdates]
  );

  // Save and delete handlers
  const handleSave = useCallback(
    async (attributeName: string) => {
      const update = attributeUpdates[attributeName];
      if (!update) return;

      // Track original group_id to detect changes
      const originalAttribute = buildingDataAttributesStore.getByName(attributeName);
      const originalGroupId = originalAttribute?.group_id;

      setSavingAttribute(attributeName);
      try {
        await buildingDataAttributesStore.updateAttribute(
          attributeName,
          update as BuildingDataAttributeUpdate
        );

        toast.success("Attribute updated successfully");
      } catch (error) {
        toast.error("Failed to update attribute");
      } finally {
        setSavingAttribute(null);
      }
    },
    [attributeUpdates, buildingDataAttributesStore]
  );

  const handleDeleteClick = useCallback((attributeName: string) => {
    setAttributeToDelete(attributeName);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!attributeToDelete) return;

    setDeletingAttribute(attributeToDelete);
    try {
      await buildingDataAttributesStore.deleteAttribute(attributeToDelete);
      toast.success("Attribute deleted successfully");
    } catch (error) {
      toast.error("Failed to delete attribute");
    } finally {
      setDeletingAttribute(null);
      setDeleteConfirmOpen(false);
      setAttributeToDelete(null);
    }
  }, [attributeToDelete, buildingDataAttributesStore]);

  // Setup virtualizer for all attributes
  const virtualizer = useVirtualizer({
    count: filteredAttributes.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 49, // Row height: 8 (py-2) + 32 (h-8) + 8 (py-2) + 1 (border-b)
    overscan: 10,
  });

  // Performance monitoring (dev mode only)
  useEffect(() => {
    if (import.meta.env.DEV) {
      performance.mark("attributes-table-start");

      return () => {
        performance.mark("attributes-table-end");
        performance.measure(
          "attributes-table-render",
          "attributes-table-start",
          "attributes-table-end"
        );

        const measure = performance.getEntriesByName("attributes-table-render")[0];
        if (measure) {
          const totalRows = buildingDataAttributesStore.attributesList.length;

          console.table({
            "Load Time": `${measure.duration.toFixed(2)}ms`,
            "Total Attributes": totalRows,
            "Filtered Attributes": filteredAttributes.length,
            Status: measure.duration < 500 ? "✅ Fast" : "⚠️ Slow",
          });

          if (measure.duration > 1000) {
            console.warn(
              `⚠️ AttributesTable loaded slowly: ${measure.duration.toFixed(2)}ms (target: <500ms)`
            );
          }
        }

        performance.clearMarks("attributes-table-start");
        performance.clearMarks("attributes-table-end");
        performance.clearMeasures("attributes-table-render");
      };
    }
  }, [buildingDataAttributesStore.attributesList.length, filteredAttributes.length]);

  // Early returns AFTER all hooks
  if (buildingDataAttributesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (buildingDataAttributesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading attributes: {buildingDataAttributesStore.error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search attributes by name, group, or type..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
        <div className="flex items-center gap-2">
          <Checkbox
            id="widget-only-filter"
            checked={widgetOnly}
            onCheckedChange={(checked) => setWidgetOnly(checked === true)}
          />
          <Label htmlFor="widget-only-filter" className="cursor-pointer whitespace-nowrap text-sm">
            Widget fields only
          </Label>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create New Attribute
        </Button>
      </div>

      <div className="rounded-md border">
        <div
          ref={parentRef}
          className="overflow-auto"
          style={{
            height: "calc(100vh - 300px)",
          }}
        >
          <div style={{ minWidth: "1600px" }}>
            {/* Header */}
            <div
              role="row"
              className="sticky top-0 z-10 grid border-b bg-background"
              style={{ gridTemplateColumns: GRID_COLUMNS }}
            >
              {[
                "Attribute Name",
                "Name (EN)",
                "Name (ET)",
                "Type",
                "Value Type",
                "Unit",
                "Widget",
                "Sort",
                "Categories",
                "Sub-Class",
                "Validation",
                "Actions",
              ].map((label) => (
                <div
                  key={label}
                  role="columnheader"
                  className="flex h-12 items-center px-2 text-sm font-medium text-muted-foreground"
                >
                  <HeaderWithTooltip label={label} />
                </div>
              ))}
            </div>

            {/* Virtualized body */}
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                position: "relative",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const attr = filteredAttributes[virtualRow.index];

                return (
                  <AttributeTableRow
                    key={virtualRow.key}
                    attr={attr}
                    attributeUpdate={attributeUpdates[attr.attribute_nm]}
                    documentCategories={documentCategories}
                    subClasses={subClasses}
                    attributeCategoryIds={attributeCategories[attr.attribute_nm] || []}
                    attributeSubClassIds={buildingDataAttributesStore.getSubClassIdsFor(
                      attr.attribute_nm
                    )}
                    validationCount={buildingDataAttributesStore.getValidationCountFor(
                      attr.attribute_nm
                    )}
                    savingAttribute={savingAttribute}
                    deletingAttribute={deletingAttribute}
                    onFieldChange={handleFieldChange}
                    onGroupChange={handleGroupChange}
                    onSave={handleSave}
                    onDelete={handleDeleteClick}
                    onCategoriesChange={handleCategoriesChange}
                    onLoadCategories={handleLoadCategories}
                    onSubClassesChange={handleSubClassesChange}
                    onManageValidation={(name) => setManagingValidation(name)}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    measureRef={virtualizer.measureElement}
                    dataIndex={virtualRow.index}
                  />
                );
              })}
            </div>

            {/* Caption */}
            <div className="mt-4 px-2 pb-2 text-sm text-muted-foreground">
              {filteredAttributes.length} of {buildingDataAttributesStore.attributesList.length}{" "}
              attributes
            </div>
          </div>
        </div>
      </div>

      {/* Create Attribute Dialog */}
      <CreateAttributeDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        title="Delete Attribute"
        description={`Are you sure you want to delete attribute "${attributeToDelete}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmVariant="destructive"
      />

      {/* Validation Rules Dialog */}
      {managingValidation && (
        <ValidationDialog
          attributeName={managingValidation}
          open={!!managingValidation}
          onClose={() => setManagingValidation(null)}
        />
      )}
    </div>
  );
});

export default AttributesTable;
